import React, { useEffect, useMemo, useState } from 'react';
import Modal from '../../ui/Modal';
import { HelpIcon } from '../../ui/HelpIcon';
import { adminClientsService, ClientListItem } from '../../../services/adminClientsService';
import { toastSuccess, toastError } from '../../../lib/toast';

const ROLE_TOOLTIP =
  "Analyste : utilisateur métier (upload, extraction, validation).\nAdmin client : analyste + gestion du tenant (users, domaines, storage).";

type Role = 'analyste' | 'admin_client';
type Assignment = Role | null;

interface Props {
  open: boolean;
  user: { id: string; email: string };
  onClose: () => void;
  onSaved: () => void;
}

const ROLE_OPTIONS: { value: '' | Role; label: string }[] = [
  { value: '', label: '—' },
  { value: 'analyste', label: 'Analyste' },
  { value: 'admin_client', label: 'Admin Client' },
];

export const ManageAccessModal: React.FC<Props> = ({ open, user, onClose, onSaved }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [clients, setClients] = useState<ClientListItem[]>([]);
  const [initialAssignments, setInitialAssignments] = useState<Map<string, Assignment>>(new Map());
  const [currentAssignments, setCurrentAssignments] = useState<Map<string, Assignment>>(new Map());

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    Promise.all([
      adminClientsService.listClients(),
      adminClientsService.listUserClients(user.id),
    ])
      .then(([allClients, userAssignments]) => {
        if (cancelled) return;
        setClients(allClients);
        const map = new Map<string, Assignment>();
        allClients.forEach((c) => map.set(c.id, null));
        userAssignments.forEach((a) => {
          map.set(a.client_id, a.role as Role);
        });
        setInitialAssignments(new Map(map));
        setCurrentAssignments(new Map(map));
      })
      .catch((err) => {
        if (!cancelled) toastError('Erreur de chargement', err);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, user.id]);

  const dirtyCount = useMemo(() => {
    let n = 0;
    currentAssignments.forEach((val, key) => {
      if (initialAssignments.get(key) !== val) n++;
    });
    return n;
  }, [initialAssignments, currentAssignments]);

  const handleRoleChange = (clientId: string, value: string) => {
    setCurrentAssignments((prev) => {
      const next = new Map(prev);
      next.set(clientId, (value as Role) || null);
      return next;
    });
  };

  const handleCancel = () => {
    setCurrentAssignments(new Map(initialAssignments));
    onClose();
  };

  const handleSave = async () => {
    if (dirtyCount === 0) {
      onClose();
      return;
    }
    setSaving(true);
    const ops: Promise<unknown>[] = [];
    currentAssignments.forEach((current, clientId) => {
      const initial = initialAssignments.get(clientId) ?? null;
      if (initial === current) return;
      if (initial === null && current !== null) {
        ops.push(adminClientsService.addUserExisting(clientId, user.id, current));
      } else if (initial !== null && current === null) {
        ops.push(adminClientsService.removeUser(clientId, user.id));
      } else if (initial !== null && current !== null) {
        ops.push(adminClientsService.updateUserRole(clientId, user.id, current));
      }
    });
    try {
      await Promise.all(ops);
      toastSuccess(`${ops.length} rattachement(s) modifié(s)`);
      onSaved();
      onClose();
    } catch (err) {
      toastError('Échec partiel ou total — vérifier les rattachements', err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen={open} onClose={handleCancel} title={`Gérer accès — ${user.email}`} size="lg">
      {loading ? (
        <div className="text-gray-500 py-4">Chargement…</div>
      ) : (
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            Sélectionnez un rôle pour chaque tenant. Choisir « — » détache l'utilisateur.
          </p>
          <div className="border rounded max-h-[60vh] overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 sticky top-0">
                <tr className="border-b">
                  <th className="text-left px-3 py-2 font-medium">Client</th>
                  <th className="text-left px-3 py-2 font-medium" data-testid="role-header">
                    Rôle
                    <HelpIcon content={ROLE_TOOLTIP} />
                  </th>
                </tr>
              </thead>
              <tbody>
                {clients.map((c) => {
                  const current = currentAssignments.get(c.id) ?? null;
                  const initial = initialAssignments.get(c.id) ?? null;
                  const dirty = initial !== current;
                  return (
                    <tr key={c.id} className={`border-b ${dirty ? 'bg-yellow-50' : ''}`}>
                      <td className="px-3 py-2">
                        <div className="font-medium">{c.name}</div>
                        <div className="text-xs text-gray-500 font-mono">{c.slug}</div>
                      </td>
                      <td className="px-3 py-2">
                        <select
                          value={current ?? ''}
                          onChange={(e) => handleRoleChange(c.id, e.target.value)}
                          data-testid={`role-select-${c.slug}`}
                          className="border border-gray-300 rounded px-2 py-1 text-sm bg-white"
                        >
                          {ROLE_OPTIONS.map((opt) => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                          ))}
                        </select>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="flex justify-end gap-2 pt-2 border-t">
            <button
              type="button"
              onClick={handleCancel}
              disabled={saving}
              className="px-4 py-2 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50"
            >
              Annuler
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={saving || dirtyCount === 0}
              className="px-4 py-2 text-sm bg-primary text-white rounded hover:bg-primary-600 disabled:opacity-50"
            >
              {saving ? 'Enregistrement…' : `Enregistrer (${dirtyCount})`}
            </button>
          </div>
        </div>
      )}
    </Modal>
  );
};

export default ManageAccessModal;
